// Data Check helpers: re-aggregate the Data Template summary built by the userData functions
// (functions/userData/dataCheck.js) to any granularity, and flag series that look wrong.
// Every date is a 'YYYY-MM-DD' string read in UTC, so no timezone can move a period.
import moment from 'moment'
import numbro from 'numbro'
import { significantMantissa } from './numberFormat'

export const GRANULARITIES = ['week', 'month', 'quarter', 'year']

const UNIT = { week: 'isoWeek', month: 'month', quarter: 'quarter', year: 'year' }
const STEP = { week: 'week', month: 'month', quarter: 'quarter', year: 'year' }

function periodStart (dateKey, gran) {
  return moment.utc(dateKey, 'YYYY-MM-DD').startOf(UNIT[gran])
}

export function periodKey (dateKey, gran) {
  return periodStart(dateKey, gran).format('YYYY-MM-DD')
}

function periodLabel (m, gran) {
  if (gran === 'week') return m.format('D MMM YY')
  if (gran === 'month') return m.format('MMM YY')
  if (gran === 'quarter') return `Q${m.quarter()} ${m.format('YY')}`
  return m.format('YYYY')
}

// Every period between the start and the end of the range, so gaps stay visible
export function buildAxis (range, gran) {
  if (!range) return []
  const axis = []
  const cursor = periodStart(range.start, gran)
  const end = periodStart(range.end, gran)
  while (!cursor.isAfter(end) && axis.length < 5000) {
    axis.push({ key: cursor.format('YYYY-MM-DD'), label: periodLabel(cursor, gran) })
    cursor.add(1, STEP[gran])
  }
  return axis
}

// For each of the sheet's dates, the index of its period in the axis
export function dateAxisIndex (sheet, axis, gran) {
  const index = {}
  axis.forEach((period, i) => { index[period.key] = i })
  return sheet.dates.map(dateKey => {
    const i = index[periodKey(dateKey, gran)]
    return i === undefined ? -1 : i
  })
}

// Points of a series, or of one dimension value of it ({ dim, value } filter)
export function seriesPoints (series, filter) {
  if (!filter) return series
  if (!series.b || !series.b[filter.dim]) return null
  return series.b[filter.dim][filter.value] || null
}

// Values aligned with the axis: null for a period with no rows, sum or mean otherwise
export function aggregate (series, points, axisLength, dateToAxis) {
  const sums = new Array(axisLength).fill(null)
  const counts = new Array(axisLength).fill(0)
  if (!points) return sums
  for (let i = 0; i < points.d.length; i++) {
    const a = dateToAxis[points.d[i]]
    if (a < 0) continue
    sums[a] = (sums[a] || 0) + points.s[i]
    counts[a] += points.c ? points.c[i] : 1
  }
  if (series.a !== 'mean') return sums
  return sums.map((sum, i) => (sum === null ? null : sum / counts[i]))
}

// Total of a sum series, or average per period of a mean series
export function seriesTotal (values, agg) {
  const present = values.filter(v => v !== null)
  if (!present.length) return null
  const sum = present.reduce((total, v) => total + v, 0)
  return agg === 'mean' ? sum / present.length : sum
}

export function seriesLabel (series) {
  const parts = series.k.filter(part => part && part.length)
  return parts.length ? parts.join(' · ') : null
}

// Readable name of a series, used for sorting and search
export function seriesKeyText (series) {
  return series.k.join(' · ')
}

export function nativeGranularity (g, bucket) {
  if (bucket === 'week') return 'week'
  if (g === null || g === undefined) return null
  if (g <= 8) return 'week'
  if (g >= 25 && g <= 35) return 'month'
  if (g >= 80 && g <= 100) return 'quarter'
  if (g >= 350 && g <= 380) return 'year'
  return null
}

export function frequencyKey (g, bucket) {
  if (bucket === 'week') return 'daily'
  const native = nativeGranularity(g, bucket)
  if (native === 'week') return 'weekly'
  if (native === 'month') return 'monthly'
  if (native === 'quarter') return 'quarterly'
  if (native === 'year') return 'yearly'
  return 'irregular'
}

export function defaultGranularity (g) {
  if (g === null || g === undefined) return 'month'
  if (g >= 350) return 'year'
  if (g >= 80) return 'quarter'
  return 'month'
}

// Granularities finer than the data would only show gaps between real data points
export function granularityAvailable (gran, g) {
  if (g === null || g === undefined) return true
  if (gran === 'week') return g < 25
  if (gran === 'month') return g < 80
  if (gran === 'quarter') return g < 350
  return true
}

function median (values) {
  if (!values.length) return null
  const sorted = values.slice().sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

// Series values at their own frequency, from their first to their last period (null = no rows)
function nativeValues (sheet, series, gran) {
  const byPeriod = {}
  let first = null
  let last = null
  for (let i = 0; i < series.d.length; i++) {
    const key = periodKey(sheet.dates[series.d[i]], gran)
    const point = byPeriod[key] || (byPeriod[key] = [0, 0])
    point[0] += series.s[i]
    point[1] += series.c ? series.c[i] : 1
    if (first === null || key < first) first = key
    if (last === null || key > last) last = key
  }
  if (first === null) return []
  const values = []
  const cursor = moment.utc(first, 'YYYY-MM-DD')
  while (cursor.format('YYYY-MM-DD') <= last && values.length < 5000) {
    const point = byPeriod[cursor.format('YYYY-MM-DD')]
    values.push(point ? (series.a === 'mean' ? point[0] / point[1] : point[0]) : null)
    cursor.add(1, STEP[gran])
  }
  return values
}

function daysBetween (a, b) {
  return moment.utc(b, 'YYYY-MM-DD').diff(moment.utc(a, 'YYYY-MM-DD'), 'days')
}

// Flags for one series, on its unfiltered totals at its own frequency.
// warning = probably a mistake in the data; info = worth a look.
export function computeFlags (sheet, series, kpiRange, sheetWeekday) {
  const flags = []
  const add = (code, severity, count) => flags.push({ code: code, severity: severity, count: count || 0 })
  const strict = sheet.group === 'kpi' || series.a === 'mean'
  const seriesGran = nativeGranularity(series.g, sheet.bucket)
  const sheetGran = nativeGranularity(sheet.g, sheet.bucket)
  const gran = seriesGran || sheetGran
  const values = gran ? nativeValues(sheet, series, gran) : []
  const present = values.filter(v => v !== null)

  if (gran) {
    const missing = values.length - present.length
    if (missing > 0) add('missingPeriods', strict ? 'warning' : 'info', missing)
  }

  if (kpiRange && sheet.name !== '1. Own KPI' && series.d.length) {
    const gap = (series.g || sheet.g || 7) * 1.5
    const first = sheet.dates[series.d[0]]
    const last = sheet.dates[series.d[series.d.length - 1]]
    if (daysBetween(kpiRange.start, first) > gap || daysBetween(last, kpiRange.end) > gap) add('shorterThanKpi', 'info')
  }

  const nonZero = present.filter(v => v !== 0).map(Math.abs)
  if (nonZero.length >= 6) {
    const med = median(nonZero)
    const threshold = strict ? 5 : 10
    const outliers = present.filter(v => v !== 0 && (Math.abs(v) > threshold * med || (series.a === 'mean' && Math.abs(v) < med / 5))).length
    if (outliers > 0) add('outlier', strict ? 'warning' : 'info', outliers)
  }

  if (series.a !== 'mean' && series.neg > 0) add('negative', 'warning', series.neg)

  const allZero = present.length > 0 && present.every(v => v === 0)
  if (allZero) {
    add('allZero', 'warning')
  } else if (sheet.group === 'kpi' && series.a !== 'mean') {
    const zeros = present.filter(v => v === 0).length
    if (zeros > 0) add('zeros', 'warning', zeros)
  }

  if (!allZero && present.length >= 6 && present.every(v => v === present[0])) add('constant', 'info')

  if (seriesGran && sheetGran && seriesGran !== sheetGran && series.g !== null) add('mixedFrequency', 'info')

  if (!sheet.bucket && seriesGran === 'week' && (series.wdc > 1 || (sheetWeekday !== null && series.wdm !== sheetWeekday))) {
    add('weekdayMismatch', 'warning')
  }

  if (series.dup > 0) add('repeatedRows', 'warning', series.dup)

  const order = { warning: 0, info: 1 }
  return flags.sort((a, b) => order[a.severity] - order[b.severity])
}

// Flags of every series of a sheet, in the same order as sheet.series
export function computeSheetFlags (sheet, kpiRange) {
  const weekdays = new Array(7).fill(0)
  sheet.series.forEach(series => {
    if (series.wdm !== undefined && series.wdm !== null) weekdays[series.wdm] += series.d.length
  })
  let sheetWeekday = null
  weekdays.forEach((count, wd) => {
    if (count > 0 && (sheetWeekday === null || count > weekdays[sheetWeekday])) sheetWeekday = wd
  })
  return sheet.series.map(series => computeFlags(sheet, series, kpiRange, sheetWeekday))
}

function addPoints (acc, points) {
  for (let i = 0; i < points.d.length; i++) {
    const point = acc[points.d[i]] || (acc[points.d[i]] = [0, 0])
    point[0] += points.s[i]
    point[1] += points.c ? points.c[i] : 1
  }
}

function toPoints (acc) {
  const d = Object.keys(acc).map(Number).sort((a, b) => a - b)
  const points = { d: d, s: d.map(i => acc[i][0]) }
  if (d.some(i => acc[i][1] !== 1)) points.c = d.map(i => acc[i][1])
  return points
}

// Every series of one metric merged into a single total per unit, breakdowns included,
// shaped like a summary series so it can be aggregated and flagged the same way.
// With a key column index, one total per value of that column (and unit) instead.
export function totalSeries (sheet, metricIndex, label, keyIndex) {
  const byKey = {}
  const totals = []
  const grouped = keyIndex !== undefined && keyIndex >= 0
  sheet.series.forEach(series => {
    if (series.m !== metricIndex) return
    const name = grouped ? (series.k[keyIndex] || '') : label
    const key = `${name}\u001e${series.u}`
    let total = byKey[key]
    if (!total) {
      total = byKey[key] = { k: [name], u: series.u, m: metricIndex, a: series.a, n: 0, neg: 0, dup: 0, acc: {}, b: {} }
      totals.push(total)
    }
    total.n += series.n || 0
    total.neg += series.neg || 0
    total.dup += series.dup || 0
    addPoints(total.acc, series)
    Object.keys(series.b || {}).forEach(dim => {
      const byValue = total.b[dim] || (total.b[dim] = {})
      Object.keys(series.b[dim]).forEach(value => {
        addPoints(byValue[value] || (byValue[value] = {}), series.b[dim][value])
      })
    })
  })
  return totals.map(total => {
    const points = toPoints(total.acc)
    const days = points.d.map(i => moment.utc(sheet.dates[i], 'YYYY-MM-DD'))
    const gaps = []
    for (let i = 1; i < days.length; i++) gaps.push(days[i].diff(days[i - 1], 'days'))
    const weekdays = new Array(7).fill(0)
    days.forEach(day => { weekdays[day.day()]++ })
    let wdm = 0
    weekdays.forEach((count, wd) => { if (count > weekdays[wdm]) wdm = wd })
    const b = {}
    Object.keys(total.b).forEach(dim => {
      b[dim] = {}
      Object.keys(total.b[dim]).forEach(value => { b[dim][value] = toPoints(total.b[dim][value]) })
    })
    return Object.assign({
      k: total.k,
      u: total.u,
      m: total.m,
      a: total.a,
      n: total.n,
      neg: total.neg,
      dup: total.dup,
      g: median(gaps),
      wdc: weekdays.filter(count => count > 0).length,
      wdm: wdm,
      b: b
    }, points)
  })
}

export function warningCount (flags) {
  return flags.filter(flag => flag.severity === 'warning').length
}

export function formatValue (value) {
  if (value === null || value === undefined || !isFinite(value)) return '–'
  return numbro(value).format({
    average: Math.abs(value) >= 1000,
    mantissa: significantMantissa(value, 1),
    optionalMantissa: true,
    trimMantissa: true
  })
}
